import { inject, Injectable, signal, NgZone, OnDestroy } from '@angular/core';
import { WindowService } from '@services/window/window.service';

@Injectable({
  providedIn: 'root',
})
export class DeviceService implements OnDestroy {
  windowService = inject(WindowService);
  private ngZone = inject(NgZone);

  // Define mobile breakpoint
  private mobileQuery: MediaQueryList | null = null;

  // Use Signal to store state, default false until we can determine value
  isMobile = signal(false);

  private listenerAttached = false;

  constructor() {
    try {
      // Only call matchMedia if it exists (guards tests/SSR mocks)
      if (
        this.windowService &&
        typeof this.windowService.matchMedia === 'function'
      ) {
        const mq = this.windowService.matchMedia('(max-width: 768px)');
        this.mobileQuery = mq;

        // Set initial value inside Angular zone so signals update properly
        this.ngZone.run(() => {
          this.isMobile.set(mq.matches);
        });

        // Attach listener outside Angular to avoid creating macrotasks that keep
        // the Zone unstable; when the media query changes we re-enter the zone
        // just to update the signal.
        this.ngZone.runOutsideAngular(() => {
          if (mq && typeof mq.addEventListener === 'function') {
            mq.addEventListener('change', this.handleMediaQueryChangeOutside);
            this.listenerAttached = true;
          }
          // Do not use deprecated addListener/removeListener API.
        });
      }
    } catch {
      // swallow errors in environments without matchMedia implementation
      this.mobileQuery = null;
      this.isMobile.set(false);
    }
  }

  // Called from outside Angular zone; re-enter zone only to update signal
  private handleMediaQueryChangeOutside = (event: MediaQueryListEvent) => {
    this.ngZone.run(() => {
      this.isMobile.set(event.matches);
    });
  };

  ngOnDestroy(): void {
    try {
      if (this.mobileQuery) {
        if (
          this.listenerAttached &&
          typeof this.mobileQuery.removeEventListener === 'function'
        ) {
          this.mobileQuery.removeEventListener(
            'change',
            this.handleMediaQueryChangeOutside,
          );
        }

        // Legacy removeListener handling removed — avoid deprecated APIs.
      }
    } catch {
      // ignore errors during cleanup
    }
  }
}
